import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiFetch, deleteProject, renameProject } from '@/lib/api';
import { getQueryData, setQueryData } from '@/lib/query';

export interface Production {
  id: string;
  status: string;
  phase: string;
  phase_status: Record<string, string>;
  config: Record<string, string>;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}

export interface Provenance {
  source_system?: string;
  agent?: string;
  on_behalf_of?: string;
  [k: string]: unknown;
}

export interface Project {
  id: string;
  title: string;
  owner_uid?: string;
  provenance?: Provenance;
  script: string;
  script_preview: string;
  creative_brief: string;
  creative_brief_preview: string;
  status: string;
  aspect_ratio?: string;
  production_type?: string;
  iconik_collection_id?: string;
  /** "default" | "no_voiceover" | "none" — who may speak (engine/crew/voice_policy.py). */
  voice_policy?: string;
  production_count: number;
  created_at: string;
  productions: Production[];
  source_script?: string;
  bible_status?: string;
  translation_report?: {
    ok: boolean;
    issues?: string[];
    scenes?: number;
    beats?: number;
    shots?: number;
    host_a_roll?: number;
    b_roll?: number;
    word_count?: number;
    estimated_runtime_seconds?: number;
  };
}

/** One row of the character / location / prop rosters — same shape for all three. */
export interface RosterEntry {
  name: string;
  description: string;
  /** Has a locked reference sheet, so the look is reused rather than redesigned. */
  locked: boolean;
  enabled: boolean;
  /** Characters only: whether the script actually calls for this person. */
  inScript?: boolean;
  /** Characters only. */
  voiceName?: string;
  /** Locations only: the authored LOC_nnn anchor. */
  locationId?: string;
}

export type RosterKind = 'characters' | 'locations' | 'props';

const ROSTER_FIELD: Record<RosterKind, string> = {
  characters: 'characters',
  locations: 'locations',
  props: 'props',
};

/** The rosters come back with per-kind field names; normalise to one shape. */
function toRosterEntry(kind: RosterKind, raw: Record<string, unknown>): RosterEntry {
  return {
    name: String(raw.name || ''),
    description: String(raw.description || ''),
    // Characters report `locked`; locations/props report `provided`.
    locked: Boolean(kind === 'characters' ? raw.locked : raw.provided),
    enabled: Boolean(raw.enabled),
    inScript: raw.in_script as boolean | undefined,
    voiceName: raw.voice_name as string | undefined,
    locationId: raw.location_id as string | undefined,
  };
}

/**
 * All project-page state: the project itself, its three rosters, the catalogs the
 * Launch card needs, and every mutation. Previously ~20 `useState` hooks and a dozen
 * inline handlers inside one 1,400-line component.
 */
const cacheKeys = {
  project: (id: string) => `project:${id}`,
  assets: (id: string) => `project:${id}:assets`,
  rosters: (id: string) => `project:${id}:rosters`,
};

export function useProject(id?: string) {
  const { t } = useTranslation('dashboard');
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<Record<string, string[]>>({});
  const [rosters, setRosters] = useState<Record<RosterKind, RosterEntry[]>>({
    characters: [],
    locations: [],
    props: [],
  });
  const [genres, setGenres] = useState<{ genre_key: string; display_name?: string; retired?: boolean }[]>([]);
  const [styles, setStyles] = useState<{ style_id: string; name?: string }[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  // Which project the state above belongs to. Right after `id` changes, one render
  // still holds the PREVIOUS project's data — the cache sync below must skip it.
  const [stateFor, setStateFor] = useState<string | undefined>(undefined);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  // The project currently shown — late responses for a previously opened project
  // are dropped (they used to land in the next project's view).
  const currentId = useRef(id);
  currentId.current = id;

  const loadRoster = useCallback(
    (kind: RosterKind) => {
      if (!id) return;
      apiFetch(`/api/projects/${id}/${kind}/roster`)
        .then((r) => r.json())
        .then((data) => {
          if (currentId.current !== id) return;
          const list = (data[ROSTER_FIELD[kind]] || []) as Record<string, unknown>[];
          setRosters((prev) => ({ ...prev, [kind]: list.map((raw) => toRosterEntry(kind, raw)) }));
        })
        .catch(() => {});
    },
    [id],
  );

  const loadAllRosters = useCallback(() => {
    loadRoster('characters');
    loadRoster('locations');
    loadRoster('props');
  }, [loadRoster]);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const fresh = await apiFetch(`/api/projects/${id}`).then((r) => r.json());
      if (currentId.current === id) setProject(fresh);
    } catch {
      /* best effort */
    }
    loadAllRosters();
  }, [id, loadAllRosters]);

  useEffect(() => {
    if (!id) return;
    // Switching projects must DROP the previous project's data (or the rail would
    // show the old project's script, cast and productions while the fetch runs).
    // Seed from THIS project's cached snapshot instead (lib/query), so reopening a
    // project renders at once and refreshes in the background; spinner only when
    // nothing is cached.
    const cachedProject = getQueryData<Project>(cacheKeys.project(id)) ?? null;
    setLoading(!cachedProject);
    setProject(cachedProject);
    setAssets(getQueryData<Record<string, string[]>>(cacheKeys.assets(id)) ?? {});
    setRosters(
      getQueryData<Record<RosterKind, RosterEntry[]>>(cacheKeys.rosters(id)) ?? { characters: [], locations: [], props: [] },
    );
    setStateFor(id);

    let cancelled = false;
    apiFetch(`/api/projects/${id}`)
      .then((r) => r.json())
      .then((data) => !cancelled && setProject(data))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    apiFetch(`/api/projects/${id}/assets`)
      .then((r) => r.json())
      .then((data) => !cancelled && setAssets(data.productions || {}))
      .catch(() => {});

    // These are slow (see below), so a fast second click could otherwise land the
    // FIRST project's response after the second one's and show the wrong project.
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Keep the cache in step with every change (fetches AND local edits), so the next
  // visit seeds from the latest known state.
  useEffect(() => {
    if (id && stateFor === id && project) setQueryData<Project>(cacheKeys.project(id), () => project);
  }, [id, stateFor, project]);
  useEffect(() => {
    if (id && stateFor === id) setQueryData(cacheKeys.assets(id), () => assets);
  }, [id, stateFor, assets]);
  useEffect(() => {
    if (id && stateFor === id) setQueryData(cacheKeys.rosters(id), () => rosters);
  }, [id, stateFor, rosters]);

  useEffect(() => {
    // Global catalogs: render the cached copy, refresh in the background.
    const cachedGenres = getQueryData<typeof genres>('catalog:genres');
    const cachedStyles = getQueryData<typeof styles>('catalog:styles');
    if (cachedGenres) setGenres(cachedGenres);
    if (cachedStyles) setStyles(cachedStyles);
    apiFetch('/api/genres')
      .then((r) => r.json())
      .then((d) => {
        setGenres(d.genres || []);
        setQueryData('catalog:genres', () => d.genres || []);
      })
      .catch(() => {});
    apiFetch('/api/styles')
      .then((r) => r.json())
      .then((d) => {
        setStyles(d.styles || []);
        setQueryData('catalog:styles', () => d.styles || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadAllRosters();
  }, [loadAllRosters]);

  // The production bible (and with it the cast) may land while this page is open —
  // keep polling until the roster has entries so the upload affordance appears
  // without a manual refresh. Footage projects have no cast to wait for.
  useEffect(() => {
    if (rosters.characters.length > 0) return;
    if ((project?.production_type || 'generative') === 'footage') return;
    const t = setInterval(() => loadRoster('characters'), 20_000);
    return () => clearInterval(t);
  }, [rosters.characters.length, project?.production_type, loadRoster]);

  const patchProject = useCallback(
    async (body: Record<string, unknown>) => {
      if (!id) return;
      try {
        await apiFetch(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
        setProject((p) => (p ? { ...p, ...body } : p));
      } catch {
        /* best effort — produce also sends these explicitly */
      }
    },
    [id],
  );

  /** Rename the project (owner only). Returns false and flashes on failure. */
  const renameProjectTitle = useCallback(
    async (title: string) => {
      const next = title.trim();
      if (!id || !next || next === project?.title) return false;
      try {
        const resp = await renameProject(id, next);
        if (!resp.ok) {
          const err = (await resp.json().catch(() => ({}))) as { detail?: string };
          flash(t('project.renameFailed', { reason: err.detail || resp.status }));
          return false;
        }
        setProject((p) => (p ? { ...p, title: next } : p));
        return true;
      } catch (e) {
        flash(t('project.renameFailed', { reason: e instanceof Error ? e.message : t('error.network') }));
        return false;
      }
    },
    [id, project?.title, flash, t],
  );

  /**
   * Delete the project and everything under it (owner only, irreversible). The caller
   * navigates away on success; on failure we stay put and say why.
   */
  const removeProject = useCallback(async () => {
    if (!id) return false;
    try {
      const resp = await deleteProject(id);
      if (!resp.ok) {
        const err = (await resp.json().catch(() => ({}))) as { detail?: string };
        flash(t('project.deleteFailed', { reason: err.detail || resp.status }));
        return false;
      }
      return true;
    } catch (e) {
      flash(t('project.deleteFailed', { reason: e instanceof Error ? e.message : t('error.network') }));
      return false;
    }
  }, [id, flash, t]);

  /** The creative brief is plain text stored on the project document itself. */
  const uploadBrief = useCallback(
    async (file: File) => {
      const text = await file.text();
      await patchProject({ creative_brief: text, creative_brief_preview: text.slice(0, 200) });
    },
    [patchProject],
  );

  const toggleEntry = useCallback(
    async (kind: RosterKind, name: string, enabled: boolean) => {
      if (!id) return;
      const resp = await apiFetch(`/api/projects/${id}/${kind}/${encodeURIComponent(name)}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }).catch(() => null);
      if (resp?.ok) {
        await refresh();
        flash(
          t('roster.toggled', {
            name: name.toUpperCase(),
            state: enabled ? t('roster.toggledOn') : t('roster.toggledOff'),
          }),
        );
      }
    },
    [id, refresh, flash, t],
  );

  const removeEntry = useCallback(
    async (kind: RosterKind, name: string) => {
      if (!id) return;
      const resp = await apiFetch(`/api/projects/${id}/${kind}/${encodeURIComponent(name)}`, {
        method: 'DELETE',
      }).catch(() => null);
      if (resp?.ok) {
        await refresh();
        flash(t('roster.removed', { name: name.toUpperCase() }));
      }
    },
    [id, refresh, flash, t],
  );

  const uploadSheet = useCallback(
    async (kind: RosterKind, name: string, file: File) => {
      if (!id || !name) return;
      try {
        const resp = await apiFetch(`/api/projects/${id}/${kind}/upload?name=${encodeURIComponent(name)}`, {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'image/png' },
          body: await file.arrayBuffer(),
        });
        if (resp.ok) {
          flash(t('roster.uploaded', { name: name.toUpperCase() }));
          await refresh();
        } else {
          const d = await resp.json().catch(() => ({}));
          flash(`${t('roster.uploadFailed')}: ${d.detail || resp.status}`);
        }
      } catch {
        flash(t('roster.uploadFailed'));
      }
    },
    [id, refresh, flash, t],
  );

  /** Lock a character's look + voice from a finished production. */
  const promoteCharacter = useCallback(
    async (productionId: string, characterName: string) => {
      if (!id) return;
      flash(t('roster.locking', { name: characterName }));
      try {
        const resp = await apiFetch(`/api/projects/${id}/characters`, {
          method: 'POST',
          body: JSON.stringify({ production_id: productionId, character_name: characterName }),
        });
        if (resp.ok) {
          await refresh();
          flash(t('roster.locked_msg', { name: characterName.toUpperCase() }));
        } else {
          const err = await resp.json().catch(() => ({}));
          flash(t('roster.lockFailed', { name: characterName, reason: err.detail || resp.status }));
        }
      } catch (e) {
        flash(
          t('roster.lockFailed', {
            name: characterName,
            reason: e instanceof Error ? e.message : 'network error',
          }),
        );
      }
    },
    [id, refresh, flash, t],
  );

  return {
    project,
    setProject,
    loading,
    assets,
    rosters,
    genres,
    styles,
    toast,
    flash,
    refresh,
    patchProject,
    renameProjectTitle,
    removeProject,
    uploadBrief,
    toggleEntry,
    removeEntry,
    uploadSheet,
    promoteCharacter,
  };
}
